import type { FileType } from "./interface";
import { isArray } from "lodash";

export function getFileExtension(file: File) {
  if (!file.name.includes(".")) {
    return "";
  }
  const extension = file.name.split(".").pop()!.toLowerCase();
  return `.${extension}`;
}

export const isAcceptFile = (
  file: File,
  accept?: string | string[]
): boolean => {
  if (accept && file) {
    const accepts = isArray(accept)
      ? accept
      : accept
          .split(",")
          .map((x) => x.trim())
          .filter((x) => x);
    const fileExtension = getFileExtension(file);
    return accepts.some((type) => {
      const typeText = type?.toLowerCase();
      const fileType = (file.type || "").toLowerCase();
      const baseFileType = fileType.split("/")[0]; // audio/mpeg => audio;

      // `${baseFileType}/${fileExtension}` === typeText
      // filetype is audio/mpeg, but accept is audio/mp3, should return true
      if (
        typeText === fileType ||
        `${baseFileType}${fileExtension.replace(".", "/")}` === typeText
      ) {
        // 类似excel文件这种
        // 比如application/vnd.ms-excel和application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
        // 本身就带有.字符的，不能走下面的.jpg等文件扩展名判断处理
        // 所以优先对比input的accept类型和文件对象的type值
        return true;
      }
      // */*,*  之类的所有类型
      if (/^\*(\/\*)?$/.test(typeText)) {
        return true;
      }
      if (typeText.includes("/*")) {
        // image/* 这种通配的形式处理
        return fileType.replace(/\/.*$/, "") === typeText.replace(/\/.*$/, "");
      }
      if (/\..*/.test(typeText)) {
        // .jpg 等后缀名
        let suffixList = [typeText];
        // accept=".jpg", jpeg后缀类型同样可以上传，反之亦然
        if (typeText === ".jpg" || typeText === ".jpeg") {
          suffixList = [".jpg", ".jpeg"];
        }
        return suffixList.includes(fileExtension);
      }
      return false;
    });
  }
  return !!file;
};

export const getFiles = (
  fileList: FileList,
  accept: string | string[] | undefined
) => {
  if (!fileList) {
    return [];
  }
  let files: File[] = [];
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList.item(i);
    if (file) {
      files.push(file);
    }
  }
  if (accept) {
    files = files.filter((file) => {
      return isAcceptFile(file, accept);
    });
  }
  return files;
};

const ACCEPT_MAP: Record<FileType, string> = {
  pdf: ".pdf",
  txt: ".txt",
  image: ".jpg,.jpeg,.png",
  docx: ".docx",
  xlsx: ".xlsx",
  csv: ".csv",
  any: "*",
};

const ACCEPT_NAME_MAP: Record<FileType, string> = {
  pdf: "PDF",
  image: "JPG, JPEG, PNG",
  txt: "TXT",
  docx: "DOCX",
  xlsx: "XLSX",
  csv: "CSV",
  any: "Any",
};

export function getAccepts(acceptTypes: FileType[]): string | undefined {
  const accept = acceptTypes.map((type) => ACCEPT_MAP[type]).join(",");
  return accept !== "" ? accept : undefined;
}

export function getAcceptNames(acceptTypes: FileType[]): string {
  return acceptTypes.map((type) => ACCEPT_NAME_MAP[type]).join(",");
}

const MB_IN_BYTES = 1024 * 1024;

export function checkFileSizeLimit(
  f: File,
  fileSizeLimitInMBs?: number
): boolean {
  if (!fileSizeLimitInMBs) {
    return true;
  }
  return f.size <= fileSizeLimitInMBs * MB_IN_BYTES;
}
